import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { Drawer } from '../ui/Drawer';
import { TextField, Toggle } from './fields';
import ContactsPanel from './ContactsPanel';
import ContactInfoDrawer, { PendingContact } from '../ContactInfoDrawer';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { formatPhoneMaybe } from '../../utils/phone';
import { getGoogleMapsLoaderOptions } from '../../config/googleMaps';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { ContactType, getContactTypes, createContact } from '../../services/contacts.service';

/**
 * Bespoke edit/create drawer for Broker/Forwarder — a CMS entity like Customers
 * and Bill Tos. Plain address fields, a large Notes textarea, readonly audit
 * (Created / Created By / Last Used), and the reusable contacts subtable.
 *
 * Name + Phone are required, and at least one contact is required on create. On
 * create the contacts are staged in memory (ContactInfoDrawer's parent-managed
 * `onSubmit`/`pendingInitial` mode) and POSTed after the broker row is inserted
 * — mirroring the Locations editor. In edit mode the contacts are persisted live
 * by the shared ContactsPanel.
 */
const TABLE = 'brokerforwarder';

// Editable scalar columns (notes is the textarea; the rest are plain inputs).
const SCALAR_KEYS = ['name', 'address', 'address2', 'city', 'state', 'zip', 'phone', 'fax', 'notes'];

const s = (v: any): string => (v == null ? '' : String(v));
const dateDisplay = (v: any): string => {
  const sv = s(v);
  return !sv || sv.startsWith('0000') ? '' : sv;
};

const LockedField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <label className="block text-sm font-medium text-muted mb-1">{label}</label>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {value === '' || value == null ? <span className="text-gray-400">—</span> : value}
    </div>
  </div>
);

const TextArea: React.FC<{ label: string; value: string; onChange: (v: string) => void; disabled?: boolean }> = ({
  label, value, onChange, disabled,
}) => (
  <div>
    <label className="block text-sm font-medium text-label mb-1">{label}</label>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} rows={6}
      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
  </div>
);

const BrokerForwarderEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [form, setForm] = useState<TablemaintRow>({});
  const [formDeleted, setFormDeleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  // Contacts. In edit mode the ContactsPanel persists live; in create mode they
  // are staged here and POSTed after the broker row exists.
  const [contactTypes, setContactTypes] = useState<ContactType[]>([]);
  const [pendingContacts, setPendingContacts] = useState<PendingContact[]>([]);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [pendingEditIndex, setPendingEditIndex] = useState<number | null>(null);

  const { isLoaded: mapsLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  const wasDeleted = s(record.deleted) === 'Y';

  useEffect(() => {
    if (!isNew) tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
    getContactTypes().then(setContactTypes).catch(() => {});
  }, [isNew, recno]);

  // Seed the form from the record.
  useEffect(() => {
    const f: TablemaintRow = {};
    SCALAR_KEYS.forEach((k) => { f[k] = s(record[k]); });
    setForm(f);
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  // Google Places appends its predictions dropdown (.pac-container) to
  // document.body, which renders BEHIND the drawer's top-layer <dialog>. Relocate
  // it into the dialog so the predictions are visible/clickable (mirrors Customers).
  useEffect(() => {
    const dialog = topRef.current?.closest('dialog');
    if (!dialog) return;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement && node.classList.contains('pac-container')) {
            dialog.appendChild(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [mapsLoaded]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  // Fill the address fields from a Places selection (address2 stays manual).
  const onPlaceChanged = () => {
    const place = acRef.current?.getPlace();
    if (!place || !place.address_components) return;
    let streetNumber = '', route = '', city = '', state = '', zip = '';
    for (const c of place.address_components) {
      const t = c.types;
      if (t.includes('street_number')) streetNumber = c.long_name;
      else if (t.includes('route')) route = c.long_name;
      else if (t.includes('locality')) city = c.long_name;
      else if (t.includes('administrative_area_level_1')) state = c.short_name;
      else if (t.includes('postal_code')) zip = c.long_name;
    }
    const street = streetNumber ? `${streetNumber} ${route}` : route;
    setForm((p) => ({
      ...p,
      address: street || p.address,
      city: city || p.city,
      state: state || p.state,
      zip: zip || p.zip,
    }));
  };

  const contactTypeLabel = useMemo(() => {
    const m = new Map<number, string>();
    contactTypes.forEach((t) => m.set(t.recno, t.type));
    return m;
  }, [contactTypes]);

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (!s(form.name).trim()) errs.push('Name is required.');
    if (!s(form.phone).trim()) errs.push('Phone is required.');
    if (isNew && pendingContacts.length < 1) errs.push('At least one contact is required.');
    return errs;
  }, [form, isNew, pendingContacts]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];
  const invalid = (k: string) => showErrors && !s(form[k]).trim();
  const contactsInvalid = showErrors && isNew && pendingContacts.length < 1;

  // Staged-contact handlers (create mode only).
  const handleAddContact = useCallback(() => { setPendingEditIndex(null); setContactDrawerOpen(true); }, []);
  const handleEditPending = useCallback((idx: number) => { setPendingEditIndex(idx); setContactDrawerOpen(true); }, []);
  const handlePendingSubmit = useCallback((data: PendingContact) => {
    setPendingContacts((prev) =>
      pendingEditIndex != null ? prev.map((p, i) => (i === pendingEditIndex ? data : p)) : [...prev, data]);
  }, [pendingEditIndex]);

  const pendingRows = useMemo(
    () => pendingContacts.map((p, i) => ({
      key: `p${i}`,
      type: p.form.contacttype != null ? contactTypeLabel.get(p.form.contacttype) || String(p.form.contacttype) : '',
      name: p.form.contactname,
      title: p.form.contacttitle,
      email: p.form.contactemail,
      onClick: () => handleEditPending(i),
    })),
    [pendingContacts, contactTypeLabel, handleEditPending],
  );

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      if (isNew) {
        const payload: TablemaintRow = {};
        SCALAR_KEYS.forEach((k) => { payload[k] = s(form[k]); });
        const res = await tablemaintService.create(TABLE, payload);
        const newRecno = res.recno;
        // Persist the contacts queued while the broker had no recno yet.
        for (const pc of pendingContacts) {
          await createContact({ tablename: TABLE, tablerecno: newRecno, ...pc.form, notifications: pc.notifications });
        }
      } else {
        const patch: TablemaintRow = {};
        SCALAR_KEYS.forEach((k) => { if (s(form[k]) !== s(record[k])) patch[k] = s(form[k]); });
        if (formDeleted !== wasDeleted) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, isNew, form, record, formDeleted, wasDeleted, recno, pendingContacts, onClose, onSaved]);

  const txt = (key: string, label: string, required = false, maxLength?: number, onBlur?: () => void) => (
    <TextField label={label} required={required} invalid={required && invalid(key)} disabled={isSaving}
      maxLength={maxLength} value={s(form[key])} onChange={(v) => setField(key, v)} onBlur={onBlur} />
  );

  const addrInputCls =
    'w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white';

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Broker/Forwarder' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add Broker/Forwarder' : `Edit Broker/Forwarder — ${s(record.name)} (#${recno})`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="60rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table="brokerforwarder" recno={recno} label={s(record.name)} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Broker/Forwarder */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Broker/Forwarder</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {txt('name', 'Name', true, 80)}
            {txt('phone', 'Phone', true, 25, () => setField('phone', formatPhoneMaybe(s(form.phone))))}
          </div>
          {/* Address lookup (Google Places) — fills Address / City / State / Zip below. */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-label mb-1">Address Lookup</label>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              {mapsLoaded ? (
                <Autocomplete onLoad={(ac) => { acRef.current = ac; }} onPlaceChanged={onPlaceChanged}
                  fields={['address_components']} types={['address']}>
                  <input type="text" placeholder="Search an address…" disabled={isSaving} className={addrInputCls} />
                </Autocomplete>
              ) : (
                <input type="text" placeholder="Loading maps…" disabled className={addrInputCls} />
              )}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">{txt('address', 'Address', false, 80)}</div>
            <div className="md:col-span-2">{txt('address2', 'Address 2', false, 80)}</div>
            {txt('city', 'City', false, 25)}
            {txt('state', 'State', false, 2)}
            {txt('zip', 'Zip', false, 10)}
            {txt('fax', 'Fax', false, 25, () => setField('fax', formatPhoneMaybe(s(form.fax))))}
          </div>
        </fieldset>

        {/* Notes */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Notes</legend>
          <TextArea label="Notes" disabled={isSaving} value={s(form.notes)} onChange={(v) => setField('notes', v)} />
        </fieldset>

        {/* Contacts — persisted live in edit mode; staged in memory while creating. */}
        {!isNew ? (
          <ContactsPanel tablename="brokerforwarder" recno={recno} />
        ) : (
          <fieldset className={`border rounded-lg p-4 ${contactsInvalid ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'}`}>
            <legend className={`px-2 text-sm font-semibold ${contactsInvalid ? 'text-red-600 dark:text-red-400' : 'text-label'}`}>
              Contacts <span className="text-red-500">*</span>
              {contactsInvalid && <span className="ml-2 text-xs font-normal">— at least one required</span>}
            </legend>
            {pendingRows.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">No contacts yet — add at least one.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted">
                    <th className="py-1 font-medium">Type</th>
                    <th className="py-1 font-medium">Name</th>
                    <th className="py-1 font-medium">Title</th>
                    <th className="py-1 font-medium">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRows.map((c) => (
                    <tr key={c.key} onClick={c.onClick}
                      className="border-t border-gray-100 dark:border-gray-700/60 cursor-pointer text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="py-1.5">{c.type}</td>
                      <td className="py-1.5">{c.name}</td>
                      <td className="py-1.5">{c.title}</td>
                      <td className="py-1.5">{c.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <button type="button" onClick={handleAddContact}
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
              <PlusIcon className="w-4 h-4" /> Add Contact
            </button>
          </fieldset>
        )}

        {/* Audit + soft-delete (edit only). */}
        {!isNew && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <LockedField label="Created" value={dateDisplay(record.createdatetime)} />
              <LockedField label="Created By" value={s(record.createdby)} />
              <LockedField label="Last Used" value={dateDisplay(record.lastuseddatetime)} />
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <Toggle label="Deleted" checked={formDeleted} disabled={isSaving} onChange={setFormDeleted} />
              <p className="mt-1 ml-7 text-xs text-muted">
                Soft-deletes the broker/forwarder — hidden from filters but preserved in history. Uncheck and save to restore.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Staged-contact editor (create mode): parent-managed, no API until the
          broker is created. */}
      {contactDrawerOpen && (
        <ContactInfoDrawer
          isOpen
          mode={pendingEditIndex != null ? 'edit' : 'create'}
          contactRecno={null}
          tablename={TABLE}
          tablerecno={0}
          contactTypes={contactTypes}
          onClose={() => setContactDrawerOpen(false)}
          onSubmit={handlePendingSubmit}
          pendingInitial={pendingEditIndex != null ? pendingContacts[pendingEditIndex] : null}
        />
      )}
    </Drawer>
  );
};

export default BrokerForwarderEditDrawer;
